import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header, TransferModal } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import { FiArrowRight, FiDownload, FiSend, FiCalendar, FiX } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import DataTable from 'react-data-table-component';
import { formatCurrency } from '../utils/currencyUtils';
import { exportToCSV } from '../utils/exportCSV';
import SkeletonTable from '../components/common/SkeletonTable';
import CustomPagination2 from '../components/CustomPagination/CustomPagination';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const statusMeta = {
  successful: { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Successful' },
  failed:     { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Failed'     },
  pending:    { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending'    },
};

const ExpenseRecord = () => {
  useKYCStatusRedirect("/expenses");
  const { user, school } = useStateContext();
  const [balance, setBalance] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterRef = useRef(null);
  const userId = user?._id;
  const schoolId = school?._id;

  // Close date filter on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const { data } = await userRequest.get(`banking/wallets/${schoolId}/balance`);
        setBalance(data.data ?? data);
      } catch (error) {
        console.log(error);
      }
    };
    if (schoolId) fetchBalance();
  }, [schoolId]);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(
          `transactions/${userId}/${schoolId}?&type=nip_transfer&limit=1000&sort_by=date&order=des`
        );
        setTransactions(data.transactions || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    if (userId && schoolId) fetchTransactions();
  }, [userId, schoolId, refresh]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    setLoading(true);
    const start = new Date(startDate).toISOString().slice(0, 10);
    const end = new Date(endDate).toISOString().slice(0, 10);
    try {
      const { data } = await userRequest.get(
        `transactions/${userId}/${schoolId}?limit=200&sort_by=date&order=des&from=${start}&to=${end}&type=nip_transfer`
      );
      setTransactions(data.transactions || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setShowDateFilter(false);
    }
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowDateFilter(false);
  };

  const columns = [
    {
      name: 'Amount',
      cell: row => <span className="font-semibold text-gray-800">{formatCurrency(row.amount)}</span>,
      exportCell: row => formatCurrency(row.amount),
    },
    {
      name: 'Transaction Fee',
      cell: row => <span className="text-gray-500">{formatCurrency(row.transaction_fee || 0)}</span>,
      exportCell: row => formatCurrency(row.transaction_fee || 0),
    },
    {
      name: 'Final Amount',
      cell: row => <span className="font-medium text-red-600">{formatCurrency(row.final_amount)}</span>,
      exportCell: row => formatCurrency(row.final_amount),
    },
    {
      name: 'Purpose',
      cell: row => <span className="text-gray-600 text-sm">{row.metadata?.reason || '—'}</span>,
      exportCell: row => row.metadata?.reason || '—',
    },
    {
      name: 'Status',
      cell: row => {
        const s = statusMeta[row?.status] || statusMeta.pending;
        return (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${s.bg} ${s.text}`}>
            {s.label}
          </span>
        );
      },
      exportCell: row => row.status || 'N/A',
    },
    {
      name: 'Date',
      cell: row => new Date(row.created_at).toLocaleDateString('en-NG', { dateStyle: 'medium' }),
      sortable: true,
    },
    {
      cell: row => (
        <Link to={`/expense-details/${row._id}`}>
          <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <FiArrowRight className="w-4 h-4" />
          </button>
        </Link>
      ),
      ignoreRowClick: true,
      button: true,
      width: '56px',
    },
  ];

  const filteredData = transactions?.filter(item =>
    item.metadata?.reason?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.amount?.toString().includes(filterText)
  ) || [];

  const hasDateFilter = startDate && endDate;

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">

      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <Header category="Transfer" title="Expense Record" />

        <div className="flex items-center gap-2 shrink-0">
          {/* Date filter */}
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setShowDateFilter(!showDateFilter)}
              className={`inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full transition-all
                ${hasDateFilter
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
            >
              <FiCalendar className="w-4 h-4" />
              {hasDateFilter ? (
                <span>
                  {new Date(startDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
                  {' — '}
                  {new Date(endDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
                </span>
              ) : 'Date Range'}
              {hasDateFilter && (
                <span
                  role="button"
                  onClick={e => { e.stopPropagation(); clearDateFilter(); }}
                  className="ml-1 p-0.5 rounded-full hover:bg-indigo-100"
                >
                  <FiX className="w-3.5 h-3.5" />
                </span>
              )}
            </button>

            {showDateFilter && (
              <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 shadow-lg rounded-2xl p-5 w-72">
                <p className="text-sm font-semibold text-gray-700 mb-4">Filter by date range</p>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1.5 text-xs font-medium text-gray-500">Start Date</label>
                    <input
                      type="date" value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="bg-gray-50 border border-gray-200 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                    />
                  </div>
                  <div>
                    <label className="block mb-1.5 text-xs font-medium text-gray-500">End Date</label>
                    <input
                      type="date" value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="bg-gray-50 border border-gray-200 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button" onClick={clearDateFilter}
                      className="flex-1 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      type="button" onClick={submitHandler}
                      disabled={loading || !startDate || !endDate}
                      className="flex-1 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
                    >
                      {loading ? 'Searching…' : 'Apply'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
          >
            <FiSend className="w-4 h-4" />
            Make a Transfer
          </button>
        </div>
      </div>

      {/* Search + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MdSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search transfers..."
            className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-48 md:w-64"
          />
        </div>
        <button
          onClick={() => exportToCSV(filteredData, columns, 'expense-transfers.csv')}
          className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
        >
          <FiDownload className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={row => row._id}
          pagination
          paginationComponent={CustomPagination2}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={5} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No transfer records found.</div>
          }
        />
      </div>

      {isModalOpen && (
        <TransferModal closeModal={() => setIsModalOpen(false)} school={school} balance={balance} onSuccess={() => setRefresh(r => !r)} />
      )}
    </div>
  );
};

export default ExpenseRecord;
